import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatError } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, GitBranch, Building2, User } from "lucide-react";

const REC_STYLE = {
  Recommended: "bg-blue-100 text-blue-700", "Client Review": "bg-indigo-100 text-indigo-700", Shortlisted: "bg-violet-100 text-violet-700",
  "Client Interview": "bg-amber-100 text-amber-700", Selected: "bg-emerald-100 text-emerald-700", Rejected: "bg-red-100 text-red-600",
  Withdrawn: "bg-slate-200 text-slate-600", "On Hold": "bg-orange-100 text-orange-700", Draft: "bg-slate-100 text-slate-500",
};
const COL_ACCENT = {
  Recommended: "border-t-blue-400", "Client Review": "border-t-indigo-400", Shortlisted: "border-t-violet-400",
  "Client Interview": "border-t-amber-400", Selected: "border-t-emerald-400", Rejected: "border-t-red-400",
};
const scoreColor = (v) => (v >= 85 ? "#10B981" : v >= 70 ? "#0055FF" : v >= 50 ? "#F59E0B" : "#EF4444");

export default function AgencyPipeline() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const load = () => api.get("/agency/pipeline").then((r) => setD(r.data)).catch(() => toast.error("Failed to load pipeline"));
  useEffect(() => { load(); }, []);

  const setStatus = async (rid, status) => {
    try { await api.put(`/agency/recommendations/${rid}`, { status }); toast.success(`Moved to ${status}`); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  if (!d) return <div className="min-h-[40vh] grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;
  const columns = d.columns;

  return (
    <div className="space-y-6" data-testid="agency-pipeline">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-ink flex items-center gap-2"><GitBranch className="h-6 w-6 text-brand" /> Client Pipeline</h1>
          <p className="text-sm text-ink-soft mt-1">Track every candidate submission across your clients and move them through the recruitment stages.</p>
        </div>
        <div className="flex gap-3">
          {[["Total", d.totals.total], ["Active", d.totals.active], ["Selected", d.totals.selected]].map(([k, v]) => (
            <div key={k} className="rounded-xl bg-white border border-slate-200 px-4 py-2 text-center"><div className="num font-extrabold text-xl text-ink">{v}</div><div className="text-[10px] text-ink-faint uppercase tracking-wide">{k}</div></div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {columns.map((col) => {
          const items = d.items.filter((i) => i.status === col.status);
          return (
            <div key={col.status} className={`bg-slate-50/60 rounded-2xl border border-slate-200 border-t-4 ${COL_ACCENT[col.status] || "border-t-slate-300"}`} data-testid={`pipeline-col-${col.status.replace(/\s+/g, "-")}`}>
              <div className="px-4 py-3 flex items-center justify-between">
                <span className="font-heading font-bold text-ink text-sm">{col.status}</span>
                <span className="num text-xs font-bold px-2 py-0.5 rounded-full bg-white border border-slate-200 text-ink-soft">{col.count}</span>
              </div>
              <div className="px-3 pb-3 space-y-2.5 max-h-[62vh] overflow-y-auto">
                {items.map((it) => (
                  <div key={it.recommendation_id} className="bg-white rounded-xl border border-slate-200 p-3.5 shadow-sm" data-testid={`pipeline-card-${it.recommendation_id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <button onClick={() => navigate(`/agency/candidates/${it.candidate_id}`)} className="text-sm font-bold text-ink hover:text-brand text-left flex items-center gap-1.5"><User className="h-3.5 w-3.5 text-ink-faint" /> {it.candidate_name}</button>
                      {it.einstein_score != null && <span className="num text-xs font-extrabold" style={{ color: scoreColor(it.einstein_score) }}>{it.einstein_score}</span>}
                    </div>
                    <div className="text-[11px] text-ink-soft mt-1.5 flex items-center gap-1"><Building2 className="h-3 w-3 text-ink-faint" /> {it.client_name}</div>
                    <div className="text-[11px] text-ink-faint truncate">{it.job_title || it.target_role_name || "—"}</div>
                    <select value={it.status} onChange={(e) => setStatus(it.recommendation_id, e.target.value)} data-testid={`pipeline-status-${it.recommendation_id}`}
                      className={`mt-2.5 w-full text-[11px] font-bold rounded-lg border border-slate-200 px-2 py-1.5 cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand ${REC_STYLE[it.status] || REC_STYLE.Draft}`}>
                      {d.statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                ))}
                {items.length === 0 && <div className="text-center text-xs text-ink-faint py-6">No candidates</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
